/**
 * IntelligencePortal.js — the Intelligence page (Lane 826).
 *
 * ONE surface listing everything a human has contributed to this company's
 * memory, across every channel. Ryan told his team that anything you tell
 * Wingman is remembered and that there is a page where you can see it and
 * delete it; there wasn't, because four of the five ways a person puts
 * knowledge in had no page at all.
 *
 * Mounted by pages/MyCaptures.js at BOTH `/intelligence` (nav) and `/captures`
 * (the pre-existing provenance deep link). It renders only when
 * INTELLIGENCE_PORTAL_ENABLED is on; with the flag off MyCaptures renders the
 * legacy chat-only list exactly as before, so turning the flag off is a real
 * rollback rather than a blank page.
 *
 * THREE THINGS THIS PAGE REFUSES TO DO, each on purpose:
 *
 *  1. It does not render an action a channel cannot honour. Interview answers
 *     and account notes are stored TWICE — as an assertion here and as a
 *     `demand_context_intelligence` row the account page renders — so a
 *     retract that removed only one copy would report success while the
 *     sentence stayed on screen. Those rows show WHERE they are managed
 *     instead. The backend refuses them too, so the UI is not the only guard.
 *  2. It does not invent an expiry date. An entry with none says so and offers
 *     to set one (pre-filled a year out); it does not display a computed date
 *     as though the system held it.
 *  3. It does not promise deletion. Retract is a soft delete and the copy says
 *     so — "stops being used, record kept" — which is what a procurement
 *     audience actually wants to hear.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../components/ToastHost';
import { colors } from '../components/AppLayout';
import {
  listIntelligence,
  retractEntry,
  setEntryExpiry,
} from '../api/intelligence';
// Saving a PENDING capture is the chat-capture lifecycle, not a portal-only
// action, so it goes through the lifecycle client that already owns it —
// `POST /api/chat/capture/confirm/{id}` -> `helpers/chat_capture.confirm_candidate`,
// the SAME entry point the in-chat chip and the legacy list call, storing at
// 0.85 per Rule 105. Writing a second portal-addressed confirm endpoint over
// the same helper would add an HTTP surface without adding a capability, and
// would put Save behind INTELLIGENCE_PORTAL_ENABLED — so a mid-session flag
// flip would turn this button into a bare 404 while the legacy page it falls
// back to still saves fine.
//
// This is the ONE import from the other client, and it is deliberate: that
// module's docstring forbids MERGING the two clients, not a page calling both.
// MyCaptures.js already imports from here for the legacy path.
import { confirmCandidate, dismissCandidate } from '../api/chatCapture';
// Lane 1430: removing a document deletes it for real (its own client; the
// removal route is not behind the Intelligence page switch).
import { removeUpload, removalErrorText } from '../api/uploadRemoval';


const PAGE_SIZE = 50;

//: How many extra pages the deep link may pull while hunting for its target.
//: `/captures?candidate_id=X` used to be trivially satisfiable because the page
//: was chat-only and the target was always inside the first 50 rows. Now that
//: five channels share one list, an older capture can sit well past page 1, and
//: the old behaviour on a miss was to SILENTLY do nothing. Bounded so a deep
//: link into a huge history cannot spin: after this many pages we say plainly
//: that we could not find it rather than pretending nothing was asked.
const DEEP_LINK_MAX_EXTRA_PAGES = 5;

const CHANNEL_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'chat', label: 'Chat' },
  { key: 'document', label: 'Documents' },
  { key: 'negotiation', label: 'Negotiation' },
  { key: 'interview', label: 'Interviews' },
  { key: 'account_note', label: 'Account notes' },
];


// ============================================================================
// Pure helpers
// ============================================================================

export function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** The one sentence describing an entry's shelf life. Never invents a date. */
export function expiryLabel(row) {
  if (!row) return '';
  if (row.expiry_state === 'set') {
    const when = formatDate(row.expires_at);
    if (!when) return 'Expiry unreadable';
    return row.is_expired ? `Expired ${when}` : `Expires ${when}`;
  }
  if (row.expiry_state === 'never') return 'Doesn’t expire';
  return 'No expiry set';
}

/** Who said it. "Unattributed" is a real answer, not a placeholder for a bug. */
export function authorLabel(row) {
  if (!row) return 'Unattributed';
  if (row.author_is_me) return 'You';
  return row.author_name || 'Unattributed';
}

/**
 * The deep-link target as an entry id.
 *
 * `?candidate_id=X` is the ORIGINAL contract (utils/provenanceChip.js builds it
 * and Home.provenance.test.js asserts it) and always meant a chat capture, so
 * it maps to `chat:X`. `?entry_id=` is the new, explicit form. Supporting both
 * is what lets the namespace change without breaking a live link.
 */
export function deepLinkTarget(searchParams) {
  const explicit = searchParams.get('entry_id');
  if (explicit) return explicit;
  const raw = searchParams.get('candidate_id');
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? `chat:${n}` : null;
}

/** The first scope the page shows. `mine` unless the link asks for `company`. */
export function initialScope(searchParams) {
  const raw = searchParams && searchParams.get ? searchParams.get('scope') : null;
  return raw === 'company' ? 'company' : 'mine';
}

/** ISO (midnight local) one year out — what the date picker starts on. */
export function defaultExpiryInput(row) {
  const suggested = row?.suggested_expires_at;
  const d = suggested ? new Date(suggested) : new Date(Date.now() + 365 * 864e5);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * The entity picker's options, derived from the rows ALREADY LOADED.
 *
 * This is deliberately a DISPLAY filter, not a query. The list endpoint takes
 * scope / channel / include_retracted / cursor / limit and has no entity
 * parameter, so the alternative would have been a backend change — out of
 * scope for a presentation lane. The consequence is stated on the page rather
 * than hidden: the picker can only offer entities it has seen, so when more
 * pages remain the page says so and points at "Load more".
 *
 * Sorted case-insensitively so the order is the one a person reading a list
 * expects, rather than newest-first bleeding through from the rows.
 */
export function entityOptionsFrom(rows) {
  const seen = new Set();
  (rows || []).forEach((r) => {
    const label = r && r.entity_label;
    if (label) seen.add(label);
  });
  return Array.from(seen).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Rows matching the entity picker. 'all' is not a filter, it is no filter. */
export function filterByEntity(rows, entity) {
  if (!entity || entity === 'all') return rows || [];
  return (rows || []).filter(r => r && r.entity_label === entity);
}


// ============================================================================
// Component
// ============================================================================

function IntelligencePortal() {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialError, setInitialError] = useState(null);
  // Lane 1342: a deep link may open on the company-wide view (?scope=company),
  // so a teammate's upload linked from the supplier dossier is findable. Only
  // the two values the API accepts; anything else falls back to 'mine'.
  const [scope, setScope] = useState(() => initialScope(searchParams));
  const [channel, setChannel] = useState('all');
  const [entity, setEntity] = useState('all');
  const [includeRetracted, setIncludeRetracted] = useState(false);
  const [expiryEditing, setExpiryEditing] = useState(null);
  const [deepLinkMissed, setDeepLinkMissed] = useState(false);

  const targetEntryId = deepLinkTarget(searchParams);
  const rowRefs = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null);

  const setRowRef = useCallback((entryId) => (node) => {
    if (node) rowRefs.current.set(entryId, node);
    else rowRefs.current.delete(entryId);
  }, []);

  const query = useMemo(() => ({
    scope,
    channels: channel === 'all' ? null : [channel],
    includeRetracted,
    limit: PAGE_SIZE,
  }), [scope, channel, includeRetracted]);

  const entityOptions = useMemo(() => entityOptionsFrom(items), [items]);
  const visibleItems = useMemo(() => filterByEntity(items, entity), [items, entity]);

  // A server-side filter change (scope / channel) swaps the whole row set, so
  // the entity that was picked can simply stop existing. Falling back to "all"
  // beats leaving a stale selection that renders an empty list while the
  // picker still names something.
  useEffect(() => {
    if (entity !== 'all' && !entityOptions.includes(entity)) setEntity('all');
  }, [entity, entityOptions]);

  // ---- Fetch ----

  const reload = useCallback(async () => {
    setLoading(true);
    setInitialError(null);
    setDeepLinkMissed(false);
    try {
      const res = await listIntelligence(query);
      setItems(res.items || []);
      setNextCursor(res.next_cursor ?? null);
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-LIST-ERROR]', err?.status, err?.detail);
      setInitialError('Couldn’t load your intelligence.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { reload(); }, [reload]);

  const loadMore = useCallback(async () => {
    if (nextCursor == null || loadingMore) return null;
    setLoadingMore(true);
    try {
      const res = await listIntelligence({ ...query, cursor: nextCursor });
      const fresh = res.items || [];
      setItems(prev => prev.concat(fresh));
      setNextCursor(res.next_cursor ?? null);
      return res;
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-LOADMORE-ERROR]', err?.status, err?.detail);
      showToast({ message: 'Couldn’t load more. Try again.', variant: 'error', durationMs: 4000 });
      return null;
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, query, showToast]);

  // ---- Deep link ----
  //
  // Hunt across pages rather than giving up on page 1. The old page could
  // assume its target was in the first 50 rows because it only ever listed one
  // channel; that assumption died when five channels started sharing the list.
  useEffect(() => {
    if (loading || !targetEntryId || items.length === 0) return undefined;
    const found = items.find(r => r.entry_id === targetEntryId);

    if (!found) {
      if (nextCursor != null && !loadingMore) {
        const pagesLoaded = Math.ceil(items.length / PAGE_SIZE);
        if (pagesLoaded <= DEEP_LINK_MAX_EXTRA_PAGES) { loadMore(); return undefined; }
      }
      if (nextCursor == null || !loadingMore) setDeepLinkMissed(true);
      return undefined;
    }

    setDeepLinkMissed(false);
    const node = rowRefs.current.get(found.entry_id);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setHighlightedId(found.entry_id);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedId(null);
      highlightTimeoutRef.current = null;
    }, 2000);

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [loading, items, targetEntryId, nextCursor, loadingMore, loadMore]);

  // ---- Actions ----

  const patchRow = useCallback((entryId, patch) => {
    setItems(prev => prev.map(r => (r.entry_id === entryId ? { ...r, ...patch } : r)));
  }, []);

  //: Drop a row that the server will no longer return. Used ONLY by dismiss:
  //: `user_rejected` is deliberately absent from `_CHAT_LIVE_STATUSES`
  //: (intelligence_portal.py), so a dismissed capture is gone from the next
  //: load. Removing it locally is what makes the page agree with the server
  //: rather than showing a row that a refresh would silently delete.
  const removeRow = useCallback((entryId) => {
    setItems(prev => prev.filter(r => r.entry_id !== entryId));
  }, []);

  // Lane 1430. Removal deletes the upload for the whole company, so the row
  // leaves the list (the server no longer returns it, even with retracted).
  const handleRemove = useCallback(async (row) => {
    try {
      const res = await removeUpload(row.candidate_id);
      removeRow(row.entry_id);
      showToast({
        message: (res && res.message) || 'Removed. Wingman no longer has this file.',
        variant: 'success', durationMs: 5000,
      });
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-REMOVE-ERROR]', err?.status, err?.detail);
      if (err?.status === 404) removeRow(row.entry_id);
      showToast({ message: removalErrorText(err), variant: 'error', durationMs: 6000 });
    }
  }, [removeRow, showToast]);

  const handleRetract = useCallback(async (row) => {
    try {
      await retractEntry(row.entry_id);
      patchRow(row.entry_id, {
        status: 'retracted', can_retract: false, can_set_expiry: false,
      });
      showToast({
        message: 'Retracted. It stops being used, and the record is kept.',
        variant: 'success', durationMs: 4000,
      });
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-RETRACT-ERROR]', err?.status, err?.detail);
      showToast({
        message: err?.status === 409
          ? (err.detail || 'That can’t be retracted here.')
          : 'Couldn’t retract. Try again.',
        variant: 'error', durationMs: 5000,
      });
    }
  }, [patchRow, showToast]);

  /**
   * Save a PENDING capture — the click that did not exist.
   *
   * ⚠️ THE PATCH BELOW MUST CLEAR `can_confirm` AND MOVE `status` OFF 'pending',
   * not merely set `assertion_id`. Those two fields are what render the "Not
   * saved yet" tag and the Save button; patching only the assertion id would
   * leave a SAVED row still labelled unsaved with a live Save button on it —
   * this page's signature untruth, inverted — and the second click would 409.
   * Rule 111: local ≡ server for every field that drives the UI, and here that
   * is four fields, not one.
   */
  const handleConfirm = useCallback(async (row) => {
    try {
      const res = await confirmCandidate(row.candidate_id);
      const assertionId = res?.assertion_id ?? null;
      patchRow(row.entry_id, {
        status: 'active',
        can_confirm: false,
        pending_reason: null,
        assertion_id: assertionId,
        // Both derived flags key off the assertion existing, exactly as
        // `_read_chat` computes them server-side. A partial write (Rule 108's
        // [CHAT-CAPTURE-PARTIAL] path) returns a null assertion_id, and then
        // neither control is offered — refusal #1: never render an action the
        // row cannot honour.
        can_retract: assertionId != null,
        can_set_expiry: assertionId != null,
      });
      showToast({
        message: 'Saved. Wingman will use this when it answers.',
        variant: 'success', durationMs: 4000,
      });
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-CONFIRM-ERROR]', err?.status, err?.detail);
      if (err?.status === 409) {
        // Someone already saved it — from the chat chip, another tab, or a
        // colleague. It IS saved, so stop offering to save it; but we were not
        // told the assertion id, so we do NOT light up Retract or Expiry on a
        // guess. They come back on the next load, from the server.
        //
        // The backend's own 409 detail is an internal string ("candidate 203
        // status=user_confirmed, expected 'pending'") — unlike the retract
        // path, whose 409s are human sentences written to be read. So this one
        // is NOT passed through.
        patchRow(row.entry_id, {
          status: 'active', can_confirm: false, pending_reason: null,
        });
        showToast({ message: 'Already saved.', variant: 'info', durationMs: 4000 });
        return;
      }
      showToast({
        message: 'Couldn’t save. Try again.',
        variant: 'error', durationMs: 5000,
      });
    }
  }, [patchRow, showToast]);

  /**
   * Decline a PENDING capture — the other half of the same decision.
   *
   * WHY THIS EXISTS AT ALL, since Retract already undoes a save: it is NOT the
   * same act, and consult-architect was right that shipping Save alone was a
   * hazard. Saving writes a live 0.85 assertion that renders VERBATIM on an
   * internal deck and feeds chat context until somebody withdraws it;
   * `dismiss_candidate` writes `user_rejected` and NO ASSERTION EVER. And
   * because `pending` is a live status while `user_rejected` is not, a
   * Save-only page offered a row whose single available verb was "commit this
   * to the supplier record" — a to-do that could never be cleared. Ryan ruled
   * for both, 2026-08-06.
   *
   * NO CONFIRMATION STEP, unlike Retract, and the difference is real: Retract
   * withdraws something already in use, whereas this declines an offer and
   * destroys nothing — Rule 104 keeps the candidate row as the audit log, which
   * is exactly what the toast says out loud.
   *
   * NOT OPTIMISTIC, unlike the legacy list's version (MyCaptures.js), which
   * removed the row first and swallowed the failure silently — so a failed
   * dismiss looked like a success until the row reappeared on the next load.
   * This page does not tell that kind of lie: the row goes when the server says
   * it went.
   */
  const handleDismiss = useCallback(async (row) => {
    try {
      await dismissCandidate(row.candidate_id);
      removeRow(row.entry_id);
      showToast({
        message: 'Dismissed. Wingman won’t use it, and the message is still on record.',
        variant: 'success', durationMs: 4000,
      });
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-DISMISS-ERROR]', err?.status, err?.detail);
      if (err?.status === 409) {
        // Already actioned somewhere else — the chat chip, another tab, a
        // colleague. Whatever happened, the OFFER is closed, so the row goes;
        // the next load shows whatever it actually became. Deliberately
        // silent: the outcome already matches what the click asked for, and
        // inventing a sentence here would be a seventh string for a case the
        // user cannot act on. Retrying would not help, so `Couldn’t dismiss`
        // would be false.
        removeRow(row.entry_id);
        return;
      }
      showToast({
        message: 'Couldn’t dismiss. Try again.',
        variant: 'error', durationMs: 5000,
      });
    }
  }, [removeRow, showToast]);

  const handleSetExpiry = useCallback(async (row, isoDate) => {
    // null is a real value meaning "never expires", not an omission.
    const payload = isoDate ? new Date(`${isoDate}T00:00:00`).toISOString() : null;
    try {
      const res = await setEntryExpiry(row.entry_id, payload);
      patchRow(row.entry_id, {
        expires_at: res?.expires_at ?? null,
        expiry_state: res?.expiry_state ?? (payload ? 'set' : 'never'),
        is_expired: false,
        suggested_expires_at: null,
      });
      setExpiryEditing(null);
      showToast({ message: 'Expiry updated.', variant: 'success', durationMs: 3000 });
    } catch (err) {
      console.log('[INTELLIGENCE-PORTAL-EXPIRY-ERROR]', err?.status, err?.detail);
      showToast({
        message: err?.status === 409
          ? (err.detail || 'That expiry can’t be changed here.')
          : 'Couldn’t update the expiry. Try again.',
        variant: 'error', durationMs: 5000,
      });
    }
  }, [patchRow, showToast]);

  // ---- Render ----

  return (
    <div style={{ padding: '32px', maxWidth: '1000px', margin: '0 auto' }}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: colors.darkText, margin: 0 }}>
          Intelligence
        </h1>
        <p style={{
          fontSize: 'var(--font-size-sm, 13px)', color: colors.textMuted,
          margin: '6px 0 0 0', lineHeight: 1.6, maxWidth: '68ch',
        }}>
          Everything people here have told Wingman: in chat, in documents you
          uploaded, in negotiation notes, in interviews, and in notes on accounts.
          Wingman uses these when it answers. Retracting one stops it being used
          and keeps the record. Removing an uploaded document deletes it.
        </p>
      </header>

      <Controls
        scope={scope} onScope={setScope}
        channel={channel} onChannel={setChannel}
        entity={entity} onEntity={setEntity} entityOptions={entityOptions}
        includeRetracted={includeRetracted} onIncludeRetracted={setIncludeRetracted}
      />

      {deepLinkMissed && (
        <div style={{
          marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
          background: 'rgba(34, 81, 255, 0.06)',
          border: `1px solid ${colors.mediumGray}`,
          fontSize: 'var(--font-size-xs, 12px)', color: colors.darkText,
        }}>
          {/* Both halves of this sentence are scope-conditional, and both used
              to be half-written. The first read "in the your list" on the
              DEFAULT scope ('mine'), because the article sat outside the
              ternary. The second told you to "try switching to Everyone's"
              even when you were ALREADY on Everyone's, where that is a
              dead-end instruction and "belongs to someone else" is not a
              reason -- at company scope you can already see everyone's. So
              each branch now says only what is true of it, and points at the
              one control that could still surface the entry. */}
          {scope === 'mine' ? (
            <>
              We couldn&rsquo;t find that entry in your list. It may have been
              retracted, or it may have been recorded under a colleague.
              Try Everyone&rsquo;s.
            </>
          ) : (
            <>
              We couldn&rsquo;t find that entry in your company&rsquo;s list.
              It may have been retracted. Turn on &ldquo;Include
              retracted&rdquo; to check.
            </>
          )}
        </div>
      )}

      <div style={{
        background: colors.white,
        border: `1px solid ${colors.mediumGray}`,
        borderRadius: '12px',
        padding: '4px 0',
        minHeight: '240px',
      }}>
        {renderBody({
          loading, initialError, reload, items, visibleItems, scope, channel,
          entity, hasMore: nextCursor !== null,
          onRetract: handleRetract, onConfirm: handleConfirm,
          onDismiss: handleDismiss, onRemove: handleRemove,
          expiryEditing, setExpiryEditing, onSetExpiry: handleSetExpiry,
          setRowRef, highlightedId,
        })}

        {(!loading && !initialError && items.length > 0 && nextCursor !== null) && (
          <div style={{
            padding: '16px 24px', borderTop: `1px solid ${colors.mediumGray}`,
            display: 'flex', justifyContent: 'center',
          }}>
            <button
              type="button" onClick={loadMore} disabled={loadingMore}
              style={{
                background: 'transparent', border: `1px solid ${colors.mediumGray}`,
                color: colors.darkText, padding: '6px 14px', borderRadius: '6px',
                fontSize: 'var(--font-size-xs, 12px)', fontWeight: 500,
                cursor: loadingMore ? 'wait' : 'pointer',
              }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Sub-renders
// ============================================================================

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        background: active ? colors.electricBlue : colors.white,
        color: active ? colors.white : colors.darkText,
        border: `1px solid ${active ? colors.electricBlue : colors.mediumGray}`,
        padding: '6px 14px', borderRadius: '100px',
        fontSize: 'var(--font-size-xs, 12px)', fontWeight: 500, cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}


function Controls({ scope, onScope, channel, onChannel,
                    entity, onEntity, entityOptions,
                    includeRetracted, onIncludeRetracted }) {
  return (
    <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Scope. "Mine" is the default on Ryan's explicit instruction: their own
          is what a person came to check; the company view is there for when
          they need it. */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: colors.textMuted, marginRight: '4px' }}>
          Showing
        </span>
        <Pill active={scope === 'mine'} onClick={() => onScope('mine')}>Mine</Pill>
        <Pill active={scope === 'company'} onClick={() => onScope('company')}>
          Everyone&rsquo;s
        </Pill>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        {CHANNEL_FILTERS.map(opt => (
          <Pill key={opt.key} active={channel === opt.key}
                onClick={() => onChannel(opt.key)}>
            {opt.label}
          </Pill>
        ))}

        {/* Entity picker. A native <select>, which is the control this app
            already uses for a "narrow the list to one of these" filter
            (Opportunities' type / status selects in `.opp-fbar`) — a page
            inventing its own popover idiom for the same job is how the last
            filter went wrong.

            WHERE IT SITS (Lane 859). It used to be pinned to the FAR RIGHT of
            the row above, the row about WHOSE entries these are. It now sits
            immediately after the channel chips, because those chips are the
            other "narrow this list" control and a person scanning the filter
            row left-to-right meets this one in that sweep instead of having to
            find it orphaned across the page in a row asking a different
            question.

            WHEN IT RENDERS (Lane 859). Any entity at all, not "more than one".
            The old bar was `> 1` on the reasoning that filtering to your only
            entity is a no-op — but a no-op RESULT is not a no-op ACTION:
            "show me only what's recorded about Aft Fluorotec" is a legitimate
            thing to ask, and answering it with a list that happens to be
            identical is a fine answer. Ryan's staging view held exactly one
            entity, so `> 1` hid the control at the precise moment he went
            looking for it.

            At ZERO entities it renders nothing rather than an inert picker
            whose only option is "All suppliers & materials" — that is refusal
            #1 in this file's header (never render an action that cannot do
            anything) and it is what the sibling warehouse filter in
            Opportunities.js already does with `warehouseOptions.length > 0`. */}
        {entityOptions.length > 0 && (
          <select
            aria-label="Filter by supplier or material"
            data-testid="entity-filter"
            value={entity}
            onChange={(e) => onEntity(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All suppliers &amp; materials</option>
            {entityOptions.map(label => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        )}

        <label style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
          fontSize: '11px', color: colors.textMuted, cursor: 'pointer',
        }}>
          <input
            type="checkbox" checked={includeRetracted}
            onChange={(e) => onIncludeRetracted(e.target.checked)}
          />
          Include retracted
        </label>
      </div>
    </div>
  );
}


function renderBody(props) {
  const {
    loading, initialError, reload, items, visibleItems, scope, channel,
    entity, hasMore,
    onRetract, onConfirm, onDismiss, onRemove, expiryEditing, setExpiryEditing, onSetExpiry,
    setRowRef, highlightedId,
  } = props;
  const rows = visibleItems || items;
  const entityFiltered = entity && entity !== 'all';

  const pad = {
    padding: '48px 24px', textAlign: 'center', color: colors.textMuted,
    fontSize: 'var(--font-size-sm, 13px)',
  };

  if (loading) return <div style={pad}>Loading&hellip;</div>;

  if (initialError) {
    return (
      <div style={pad}>
        <div style={{ marginBottom: '12px', color: colors.darkText, fontWeight: 500 }}>
          {initialError}
        </div>
        <button
          type="button" onClick={reload}
          style={{
            background: colors.white, border: `1px solid ${colors.mediumGray}`,
            color: colors.darkText, padding: '6px 14px', borderRadius: '6px',
            fontSize: 'var(--font-size-xs, 12px)', fontWeight: 500, cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (items.length === 0) return <EmptyState scope={scope} channel={channel} />;

  // There is deliberately NO "nothing matches that name" state below, because
  // it cannot happen: the picker's options are derived from `items`, and the
  // component resets a selection that stops appearing in them. A non-empty
  // `items` therefore always yields a non-empty `rows`. If you find yourself
  // adding that state back, the thing to check first is whether that reset
  // still runs — an unreachable branch would hide its absence rather than
  // report it.
  return (
    <div>
      {/* The picker only knows the entities it has SEEN (see entityOptionsFrom).
          Rather than let that read as "this is everything about them", say it
          where it matters — only while a name is actually selected and there
          are genuinely more pages. */}
      {entityFiltered && hasMore && (
        <div style={{
          padding: '8px 24px 0', fontSize: '11px', color: colors.textMuted,
          lineHeight: 1.5,
        }}>
          Showing loaded entries for {entity}. Older ones may not be loaded yet.
          Use Load more below.
        </div>
      )}
      {rows.map(row => (
        <EntryRow
          key={row.entry_id}
          row={row}
          onRetract={onRetract}
          onConfirm={onConfirm}
          onDismiss={onDismiss}
          onRemove={onRemove}
          expiryEditing={expiryEditing}
          setExpiryEditing={setExpiryEditing}
          onSetExpiry={onSetExpiry}
          rowRef={setRowRef ? setRowRef(row.entry_id) : undefined}
          highlighted={highlightedId === row.entry_id}
        />
      ))}
    </div>
  );
}


function EntryRow({ row, onRetract, onConfirm, onDismiss, onRemove, expiryEditing,
                    setExpiryEditing, onSetExpiry, rowRef, highlighted }) {
  const [confirming, setConfirming] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const retracted = row.status === 'retracted';
  // An OFFER, not a record. Read off the row rather than off `status` alone so
  // the backend stays the single decider of what a pending row may do — and so
  // a row whose label applies but whose ACTION does not (a colleague's pending
  // capture, at company scope) still reads as unsaved.
  const notSavedYet = Boolean(row.pending_reason) && !retracted;
  const editingThis = expiryEditing === row.entry_id;
  const [dateValue, setDateValue] = useState(() => defaultExpiryInput(row));

  return (
    <div
      ref={rowRef}
      data-row-entry-id={row.entry_id}
      data-highlighted={highlighted ? 'true' : undefined}
      style={{
        padding: '11px 20px',
        borderTop: `1px solid ${colors.mediumGray}`,
        backgroundColor: highlighted ? 'rgba(34, 81, 255, 0.08)' : undefined,
        transition: 'background-color 0.4s ease-out',
        opacity: retracted ? 0.62 : 1,
      }}
    >
      {/* Two columns. Everything a person READS is on the left; everything they
          can DO is on the right. Before this the channel tag, the statement,
          the detail line (for negotiation commentary, the ruled-out reason) and
          the expiry each occupied their own stacked block, so a one-sentence
          entry cost four rows of vertical space and a list of six filled a
          screen. Nothing was removed — the tags and the detail moved onto the
          metadata line, which was already there and had room. */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          {/* The statement leads now. Quoted when it is the person's own words
              — that distinction is the most honest thing on the page and is
              why `reason_verbatim` is preferred over any composed summary. */}
          <div style={{
            fontSize: 'var(--font-size-sm, 14px)', color: colors.darkText,
            lineHeight: 1.45,
            fontStyle: row.statement_is_verbatim ? 'italic' : 'normal',
          }}>
            {row.statement_is_verbatim ? `“${row.statement}”` : row.statement}
          </div>

          <div style={{
            display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
            fontSize: '11px', color: colors.textMuted, marginTop: '5px',
          }}>
            <Tag>{row.channel_label}</Tag>
            {row.entity_label && <Tag tone="entity">{row.entity_label}</Tag>}
            {retracted && <Tag tone="muted">Retracted</Tag>}
            {/* The tag this page was missing. Every other tag here reports
                something that HAPPENED to a stored entry; this one reports
                that nothing was stored at all, so it is the only one that
                carries colour — an unsaved sentence sitting silently among
                saved ones is the defect, and muted styling would reproduce it. */}
            {notSavedYet && <Tag tone="pending">Not saved yet</Tag>}
            {row.is_expired && !retracted && <Tag tone="muted">Expired</Tag>}
            <span>{authorLabel(row)}</span>
            {formatDate(row.created_at) && <span>{formatDate(row.created_at)}</span>}
            {row.detail && <span>{row.detail}</span>}
            {/* Shares the metadata line with the read-only sentence below for
                the same reason: it is an explanation, not a control. The page
                header promises "Wingman uses these when it answers", which is
                false of this row, and this is where that gets corrected. */}
            {notSavedYet && (
              <span style={{ fontStyle: 'italic' }}>{row.pending_reason}</span>
            )}

            {/* The read-only sentence stays on the LEFT on purpose: it is an
                explanation, not a control, and it is the longest string on the
                row — parked on the right it would wrap to three lines and undo
                the height this layout just bought. */}
            {!row.can_retract && !retracted && (row.read_only_reason || row.expiry_read_only_reason) && (
              <span title={row.read_only_reason || row.expiry_read_only_reason}
                    style={{ fontStyle: 'italic' }}>
                {row.read_only_reason || row.expiry_read_only_reason}
              </span>
            )}
          </div>
        </div>

        {/* Controls. The expiry is the control rather than a label beside one:
            the button SHOWS the current state and clicking it opens the editor.
            Its accessible name still says what the click does, so a screen
            reader is told "Set an expiry" and not just "No expiry set".
            A channel that cannot honour an expiry change renders plain text —
            refusal #1 in this file's header: never render an action a channel
            cannot honour. */}
        <div style={{
          flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '11px', color: colors.textMuted,
        }}>
          {/* An unsaved row shows NO expiry text at all. "No expiry set" is
              true of a stored entry that has no shelf life; on a row nothing
              was stored for, it reports a property of a record that does not
              exist — the same species of untruth as the missing tag. */}
          {!notSavedYet && (
            row.can_set_expiry && !editingThis ? (
              <button
                type="button"
                aria-label={row.expiry_state === 'set' ? 'Change expiry' : 'Set an expiry'}
                onClick={() => {
                  setDateValue(defaultExpiryInput(row));
                  setExpiryEditing(row.entry_id);
                }}
                style={ctrlBtn}
              >
                {expiryLabel(row)}
              </button>
            ) : (
              <span style={{ whiteSpace: 'nowrap' }}>{expiryLabel(row)}</span>
            )
          )}

          {/* The control this lane exists for. Primary styling, because it is
              the one thing on this page that is being ASKED of the reader —
              every other control here is optional housekeeping on something
              already kept. */}
          {row.can_confirm && (
            <button type="button" onClick={() => onConfirm(row)} style={primaryBtn}>
              Save
            </button>
          )}

          {/* Gated on the SAME flag as Save, and that is the modelling, not a
              shortcut: `can_confirm` answers "may this person act on this
              pending offer?", and there is no state in which someone may accept
              an offer but not decline it. A second `can_dismiss` field would be
              a second thing to keep in sync for a distinction that does not
              exist. Secondary styling — declining is not the ask. */}
          {row.can_confirm && (
            <button type="button" onClick={() => onDismiss(row)} style={ctrlBtn}>
              Dismiss
            </button>
          )}

          {row.can_retract && !confirming && (
            <button type="button" onClick={() => setConfirming(true)} style={ctrlBtn}>
              Retract
            </button>
          )}

          {row.can_remove && onRemove && !confirmingRemove && (
            <button type="button" onClick={() => setConfirmingRemove(true)} style={ctrlBtn}>
              Remove file
            </button>
          )}
        </div>
      </div>

      {editingThis && (
        <div style={{
          marginTop: '10px', display: 'flex', gap: '8px',
          alignItems: 'center', flexWrap: 'wrap',
        }}>
          <input
            type="date" value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            aria-label="Expiry date"
            style={{
              border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
              padding: '5px 8px', fontSize: '12px', color: colors.darkText,
            }}
          />
          <button type="button" style={primaryBtn}
                  onClick={() => onSetExpiry(row, dateValue)}>
            Save
          </button>
          <button type="button" style={linkBtn}
                  onClick={() => onSetExpiry(row, null)}>
            Never expires
          </button>
          <button type="button" style={linkBtn}
                  onClick={() => setExpiryEditing(null)}>
            Cancel
          </button>
        </div>
      )}

      {confirmingRemove && (
        <div style={{
          marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
          background: '#FEF2F2', border: `1px solid ${colors.mediumGray}`,
        }}>
          <div style={{ fontSize: '12px', color: colors.darkText, marginBottom: '8px' }}>
            Remove &ldquo;{row.statement}&rdquo; for everyone in your company?
            Wingman deletes the file, the table it stored from it and its links
            to your chats, and stops using anything it learned from it.
            This cannot be undone.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button"
                    style={{ ...primaryBtn, background: '#DC2626', borderColor: '#DC2626' }}
                    onClick={() => { setConfirmingRemove(false); onRemove(row); }}>
              Remove file
            </button>
            <button type="button" style={linkBtn}
                    onClick={() => setConfirmingRemove(false)}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <div style={{
          marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
          background: colors.lightGray || '#f6f7f9',
          border: `1px solid ${colors.mediumGray}`,
        }}>
          <div style={{ fontSize: '12px', color: colors.darkText, marginBottom: '8px' }}>
            Retract this? Wingman stops using it when it answers.
            The record is kept, so you can still see it here under
            &ldquo;Include retracted&rdquo;.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={primaryBtn}
                    onClick={() => { setConfirming(false); onRetract(row); }}>
              Retract
            </button>
            <button type="button" style={linkBtn}
                    onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function Tag({ children, tone }) {
  const palette = {
    entity: { bg: 'rgba(34, 81, 255, 0.08)', fg: colors.electricBlue },
    muted: { bg: 'transparent', fg: colors.textMuted },
    // design-system.md §Semantic: --color-warning #D97706 on --color-warning-light
    // #FFFBEB. `colors` (components/AppLayout) exports the foreground as
    // `colors.warning` but has no `-light` member, so the background is the
    // documented literal — the same shape CaptureChip.js uses for successLight
    // ('#ECFDF5'). Warning, not danger: an unsaved capture is something to
    // attend to, not something that went wrong.
    pending: { bg: '#FFFBEB', fg: colors.warning },
  }[tone] || { bg: colors.lightGray || '#f2f3f5', fg: colors.textMuted };
  return (
    <span style={{
      fontSize: '11px', fontWeight: 500, color: palette.fg,
      background: palette.bg, border: `1px solid ${colors.mediumGray}`,
      padding: '2px 8px', borderRadius: '100px', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}


const linkBtn = {
  background: 'transparent', border: 'none', padding: 0,
  color: colors.electricBlue, fontSize: '11px', fontWeight: 500,
  cursor: 'pointer', textDecoration: 'underline',
};

const primaryBtn = {
  background: colors.electricBlue, border: `1px solid ${colors.electricBlue}`,
  color: colors.white, padding: '5px 12px', borderRadius: '6px',
  fontSize: '12px', fontWeight: 500, cursor: 'pointer',
};

/** A row's right-hand controls — small, bordered, obviously clickable. */
const ctrlBtn = {
  background: colors.white, border: `1px solid ${colors.mediumGray}`,
  color: colors.darkText, padding: '3px 9px', borderRadius: '6px',
  fontSize: '11px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
};

/**
 * The entity picker. A native <select> styled like the app's other list
 * filters (Opportunities' `.opp-sel`) — same border, radius and drawn chevron
 * — rather than a new popover idiom for a job the app already has a control
 * for. Inline because this page carries no stylesheet of its own.
 */
const selectStyle = {
  font: 'inherit', fontSize: '12px', color: colors.darkText,
  border: `1px solid ${colors.mediumGray}`, background: colors.white,
  borderRadius: '6px', padding: '5px 28px 5px 10px', cursor: 'pointer',
  appearance: 'none', maxWidth: '260px',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%238A9BAE' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 9px center',
};


/**
 * The empty state is part of the build, not an afterthought.
 *
 * An empty page is exactly what Ryan hit, and the reason it was empty was not
 * "you haven't told us anything" — it was that four channels had nowhere to
 * appear. So this copy never says "you have no intelligence"; it says what was
 * looked at and what to try, and it distinguishes a genuinely empty record
 * from a filter that excluded everything.
 */
function EmptyState({ scope, channel }) {
  const card = {
    margin: '24px', padding: '28px',
    color: colors.darkText,
  };
  const head = { fontSize: 'var(--font-size-md, 15px)', fontWeight: 600, marginBottom: '10px' };
  const para = {
    fontSize: 'var(--font-size-sm, 13px)', color: colors.textMuted,
    lineHeight: 1.6, margin: '0 0 10px 0', maxWidth: '62ch',
  };

  if (channel !== 'all') {
    const label = (CHANNEL_FILTERS.find(c => c.key === channel) || {}).label || channel;
    return (
      <div style={card}>
        <div style={head}>Nothing here from {label.toLowerCase()} yet.</div>
        <p style={para}>
          {scope === 'mine'
            ? 'Try Everyone’s, or switch back to All to see the other channels.'
            : 'Switch back to All to see the other channels.'}
        </p>
      </div>
    );
  }

  if (scope === 'mine') {
    return (
      <div style={card}>
        <div style={head}>You haven&rsquo;t added anything Wingman has kept yet.</div>
        <p style={para}>
          This page fills up as you work. Anything you tell Wingman that it decides
          is worth remembering lands here, and so does everything else you put
          in:
        </p>
        <ul style={{ ...para, paddingLeft: '20px' }}>
          <li>a supplier letter or quote you upload in chat</li>
          <li>a note you make while preparing a negotiation, killing an
              argument, or saying how much leverage you have</li>
          <li>a fact you state in chat, like <em>&ldquo;Parker raised O-ring prices
              8% in January&rdquo;</em></li>
          <li>your answers in an interview, and notes written on an account</li>
        </ul>
        <p style={para}>
          Not every message becomes an entry. Wingman only keeps things that
          look like durable facts about suppliers, materials or accounts.
          If you expected something here, try <strong>Everyone&rsquo;s</strong>:
          it may have been recorded under a colleague.
        </p>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={head}>Nothing has been recorded for your company yet.</div>
      <p style={para}>
        Once anyone here uploads a document, prepares a negotiation, states a fact
        in chat, completes an interview or writes a note on an account, it will be
        listed here with who added it and when.
      </p>
    </div>
  );
}


export default IntelligencePortal;
